import { state } from "../state";
import { SpineEntry, RuntimeVersion } from "../types";
import { getRuntimeScriptUrl, getRuntimeCssUrl, mapSkeletonVersionToRuntime } from "../utils/spine-utils";
import { prepareMetaForEntry } from "./spine-loader";
import { updateSpineInfoBox, updateSpineInfoBoxError } from "../ui/info-box";
import { resetControls } from "../ui/controls";

export function handleSpineError(entryId: string, message: string) {
    const metaForEntry = state.spineMetaById[entryId];
    const currentRuntime = metaForEntry?.runtimeVersion;
    const msgLower = String(message).toLowerCase();
    
    console.log(`Spine error for ${entryId}:`, message);

    const isAtlasFormatError =
        msgLower.includes("invalid line") ||
        msgLower.includes("unknown texture filter");

    if (
        currentRuntime === "3.8" &&
        isAtlasFormatError &&
        !metaForEntry?.alternateRuntimeTried
    ) {
        updateMetaAndReload(entryId, "4.1");
        return;
    }

    const isBinaryError =
        msgLower.includes("string in string table must not be null") ||
        msgLower.includes("invalid skeleton data");
    
    if (
        isBinaryError &&
        !metaForEntry?.alternateRuntimeTried
    ) {
         updateMetaAndReload(entryId, "3.8");
         return;
    }

    updateSpineInfoBoxError(entryId, message);
}

export function updateMetaAndReload(entryId: string, newRuntime: RuntimeVersion) {
    const meta = state.spineMetaById[entryId];
    if (meta) {
        meta.runtimeVersion = newRuntime;
        meta.alternateRuntimeTried = true;
        state.spineMetaById[entryId] = meta;
        
        const entry = state.spineEntries.find(e => e.id === entryId);
        if (entry) {
            console.log(`Fallback: Reloading entry ${entryId} with runtime ${newRuntime}`);
            void createPlayer(entry);
        }
    }
}

export async function createPlayer(entry: SpineEntry) {
  try {
    const container = document.getElementById("spine-player-container");
    if (!container) return;

    if (state.currentIframe) {
        try {
            container.removeChild(state.currentIframe);
        } catch {}
        state.currentIframe = null;
    }
    state.currentPlayerProxy = null;

    const meta = await prepareMetaForEntry(entry);
    const runtimeVersion = meta.runtimeVersion ?? mapSkeletonVersionToRuntime(meta.version ?? null);
    meta.runtimeVersion = runtimeVersion;
    state.spineMetaById[entry.id] = meta;

    console.log(`Entry ${entry.id}: Using runtime ${runtimeVersion} (Version detected: ${meta.version})`);

    const config: any = {};
    
    if (meta.skeletonDataUri) {
        config.skelUrl = meta.skeletonDataUri;
    } else if (entry.skelUrl) {
        config.skelUrl = /^[a-z]+:\/\//i.test(entry.skelUrl) ? entry.skelUrl : encodeURI(entry.skelUrl);
    }
    
    if (entry.jsonUrl) config.jsonUrl = /^[a-z]+:\/\//i.test(entry.jsonUrl) ? entry.jsonUrl : encodeURI(entry.jsonUrl);
    
    if (meta.atlasDataUri) {
        config.atlasUrl = meta.atlasDataUri;
    } else if (entry.atlasUrl) {
        config.atlasUrl = /^[a-z]+:\/\//i.test(entry.atlasUrl) ? entry.atlasUrl : encodeURI(entry.atlasUrl);
    }
    
    if (entry.rawDataURIs) config.rawDataURIs = entry.rawDataURIs;
    
    if (meta.rawDataURIs) {
        config.rawDataURIs = { ...(config.rawDataURIs || {}), ...meta.rawDataURIs };
    }

    config.showControls = false;
    config.premultipliedAlpha = state.pmaEnabled;

    if (meta.atlasDataUri && entry.atlasUrl && !config.atlasUrl) {
      if (!config.rawDataURIs) config.rawDataURIs = {};
      config.rawDataURIs[entry.atlasUrl] = meta.atlasDataUri;
    }

    resetControls();

    const iframe = document.createElement("iframe");
    iframe.src = "player-sandbox.html";
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.border = "none";
    iframe.allow = "autoplay"; 
    
    container.appendChild(iframe);
    state.currentIframe = iframe;

    const welcomeScreen = document.getElementById("welcome-screen");
    if (welcomeScreen) {
        welcomeScreen.style.display = "none";
    }

    const topControls = document.getElementById("top-controls");
    const rightControls = document.getElementById("right-controls");
    const bottomPanel = document.getElementById("bottom-panel");
    const topLeftHints = document.getElementById("top-left-hints");
    
    if (topControls) topControls.style.display = "flex";
    if (rightControls) rightControls.style.display = "flex";
    if (bottomPanel) bottomPanel.style.display = "flex";
    if (topLeftHints) topLeftHints.style.display = "flex";

    iframe.onload = () => {
        const runtimeUrl = getRuntimeScriptUrl(runtimeVersion);
        const runtimeCssUrl = getRuntimeCssUrl(runtimeVersion);
        iframe.contentWindow?.postMessage({
            type: 'init',
            payload: {
                runtimeUrl,
                runtimeCssUrl,
                spineConfig: config
            }
        }, '*');
    };

    updateSpineInfoBox(entry.id); 
  } catch (error) {
    updateSpineInfoBoxError(entry.id, String(error));
  }
}
